import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { getWebinars, registerWebinar } from '../../services/api';
import {
  Video,
  Users,
  Calendar,
  Clock,
  CalendarPlus
} from 'lucide-react';
import './WebinarsPage.css';

const WebinarsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState({});
  const [stats, setStats] = useState({
    upcoming: 0,
    totalRegistrations: 0,
    totalSessions: 0
  });

  const fetchWebinars = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getWebinars({ limit: 100, sort: '-scheduledAt' });
      const webinarData = response.data.data.webinars || [];
      setWebinars(webinarData);

      // Calculate stats
      const upcoming = webinarData.filter(w => w.is_upcoming).length;
      const totalRegistrations = webinarData.reduce((sum, w) => sum + (w.enrolled_count || 0), 0);

      setStats({
        upcoming,
        totalRegistrations,
        totalSessions: webinarData.length
      });
    } catch (error) {
      console.error('Error fetching webinars:', error);
      setWebinars([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWebinars();
  }, [fetchWebinars]);

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to get countdown time
  const getTimeUntil = (scheduledAt) => {
    const now = new Date();
    const scheduled = new Date(scheduledAt);
    const diff = scheduled - now;

    if (diff <= 0) return null;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Handle webinar card click
  const handleWebinarClick = (webinarId) => {
    navigate(`/webinars/${webinarId}`);
  };

  // Handle register button click
  const handleRegister = async (e, webinarId) => {
    e.stopPropagation(); // Prevent card click

    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setRegistering(prev => ({ ...prev, [webinarId]: true }));
      await registerWebinar(webinarId);
      fetchWebinars(); // Refresh data
    } catch (error) {
      console.error('Error registering:', error);
      alert(error.response?.data?.message || 'Error registering for webinar');
    } finally {
      setRegistering(prev => ({ ...prev, [webinarId]: false }));
    }
  };

  // Helper function to add to calendar
  const addToCalendar = (e, webinar) => {
    e.stopPropagation(); // Prevent card click

    // Create iCal format
    const start = new Date(webinar.scheduled_at);
    const end = new Date(start.getTime() + webinar.duration_minutes * 60000);

    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const icalData = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      `DTSTART:${formatDate(start)}`,
      `DTEND:${formatDate(end)}`,
      `SUMMARY:${webinar.title}`,
      `DESCRIPTION:${webinar.description || ''}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\n');

    const blob = new Blob([icalData], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'webinar.ics';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="webinars-page">
      {/* Clean Header - NO background gradient */}
      <div className="webinars-header">
        <h1>Live Webinars & Streaming</h1>
        <p className="subtitle">Join live trading sessions and learn from expert traders</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green">
            <Video size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Upcoming Webinars</p>
            <h3 className="stat-value">{stats.upcoming}</h3>
            <p className="stat-description">Live sessions scheduled</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blue">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Registrations</p>
            <h3 className="stat-value">{stats.totalRegistrations}</h3>
            <p className="stat-description">Students registered</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Sessions</p>
            <h3 className="stat-value">{stats.totalSessions}</h3>
            <p className="stat-description">All time webinars</p>
          </div>
        </div>
      </div>

      {/* Webinars Grid */}
      <div className="webinars-section">
        <div className="section-header">
          <h2>All Webinars</h2>
        </div>

        {webinars.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <Video size={64} strokeWidth={1.5} />
            </div>
            <h3>No webinars scheduled</h3>
            <p>Check back later for upcoming live trading sessions</p>
          </div>
        ) : (
          <div className="webinars-grid">
            {webinars.map((webinar) => {
              const upcoming = webinar.is_upcoming;
              const countdown = upcoming ? getTimeUntil(webinar.scheduled_at) : null;
              const isLive = webinar.is_live;

              return (
                <div
                  key={webinar.id}
                  className="webinar-card"
                  onClick={() => handleWebinarClick(webinar.id)}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Thumbnail with 16:9 aspect ratio */}
                  <div className="webinar-thumbnail">
                    {webinar.thumbnail_url ? (
                      <img src={webinar.thumbnail_url} alt={webinar.title} />
                    ) : (
                      <div className="placeholder-thumbnail">
                        <Video size={48} />
                      </div>
                    )}

                    {/* Status Badge */}
                    <div className={`status-badge ${isLive ? 'live' : upcoming ? 'live-soon' : 'ended'}`}>
                      {isLive ? 'LIVE NOW' : upcoming ? 'Live Soon' : 'Ended'}
                    </div>

                    {/* Category Badge */}
                    {webinar.category && (
                      <div className="category-badge">
                        {webinar.category}
                      </div>
                    )}
                  </div>

                  {/* Webinar Content */}
                  <div className="webinar-content">
                    <h3 className="webinar-title">{webinar.title}</h3>
                    <p className="webinar-description">
                      {webinar.description || 'Join this live trading session'}
                    </p>

                    {/* Info Section */}
                    <div className="webinar-info">
                      <div className="info-item">
                        <Calendar size={16} />
                        <span>{formatDate(webinar.scheduled_at)}</span>
                      </div>
                      <div className="info-item">
                        <Clock size={16} />
                        <span>{formatTime(webinar.scheduled_at)} • {webinar.duration_minutes} min</span>
                      </div>
                      <div className="info-item">
                        <Users size={16} />
                        <span>{webinar.instructor_name || 'Instructor'}</span>
                      </div>
                    </div>

                    {/* Countdown Timer */}
                    {countdown && (
                      <div className="countdown-timer">
                        <Clock size={16} />
                        <span>Starts in: {countdown}</span>
                      </div>
                    )}

                    {/* Registration Count */}
                    <div className="registration-count">
                      <Users size={16} />
                      <span>{webinar.enrolled_count || 0} registered</span>
                    </div>

                    {/* Action Buttons */}
                    <div className="webinar-actions">
                      {isLive ? (
                        <button className="btn-watch-live">
                          Watch Live
                        </button>
                      ) : upcoming ? (
                        <>
                          {webinar.is_registered ? (
                            <button className="btn-registered" disabled>
                              Registered ✓
                            </button>
                          ) : (
                            <button
                              className="btn-register"
                              onClick={(e) => handleRegister(e, webinar.id)}
                              disabled={registering[webinar.id]}
                            >
                              {registering[webinar.id] ? 'Registering...' : 'Register'}
                            </button>
                          )}
                          <button
                            className="btn-calendar"
                            onClick={(e) => addToCalendar(e, webinar)}
                            aria-label="Add to calendar"
                          >
                            <CalendarPlus size={20} />
                          </button>
                        </>
                      ) : (
                        <button className="btn-ended" disabled>
                          Webinar Ended
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebinarsPage;
